import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import { User } from '../../gen/model/user';
import { Logger } from '../../util/logger';
import { StandaloneComponent } from '../standalone.component';
import {CountriesService} from '../countries-service';
import {Observable, Subscription} from 'rxjs';
import {AppComponent} from '../app.component';
import {TranslateService} from '@ngx-translate/core';
import {AppConstants} from '../AppConstants';
import {ConfigurationService} from '../ui-configuration/configuration-service';
import {SsoUIField} from '../ui-configuration/ssoui-configuration';
import {AppStateService, AuthenticationState} from '../app-state.service';
import {AlertHandler} from '../no-concurrent-alerts-handler';
import {
  AcceptAgreementChallengeWithReceivedTime, listUsersAgreementsWithReceivedTime
} from '../../util/AgreementUtils';
import {AgreementService} from '../../gen';
import {formatDate} from '@angular/common';
import {RegionService} from '../region-service';
import {getOneTrustLink} from '../../env-specific';
const cloneDeep = require('lodash.clonedeep');


@Component({
  selector: 'app-view-profile',
  templateUrl: './view-profile.component.html',
  styleUrls: ['./view-profile.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ViewProfileComponent extends StandaloneComponent implements OnInit, OnDestroy {

  personalFields: SsoUIField[];
  contactFields: SsoUIField[];
  loginFields: SsoUIField[];
  hasEditableFields: boolean;
  hasTOTP: boolean;
  notification: { key: string, data?: any } | undefined;
  userAgreements: AcceptAgreementChallengeWithReceivedTime[] | undefined;
  noAgreementsFound: boolean;
  agreementLoadingFailed: boolean;
  agreementLoadingFailedDismissed: boolean;

  profile: User;
  hasDelegatedAuthentication = false;
  private navigationEndSubscription: Subscription;
  constructor(
    @Inject('AgreementApi') protected agreementApi: AgreementService,
    protected appStateService: AppStateService,
    route: ActivatedRoute,
    router: Router,
    protected countries: CountriesService,
    protected regions: RegionService,
    protected logger: Logger,
    protected translate: TranslateService,
    private alertHandler: AlertHandler,
    hostElement: ElementRef,
    configuration: ConfigurationService) {
  super(route, router, hostElement, configuration);
    this.focusSelectorPrefix = '.card-body';
    this.personalFields = cloneDeep(this.configuration.getProperties().profilePersonalFields);
    this.contactFields = cloneDeep(this.configuration.getProperties().profileContactFields);
    this.loginFields = cloneDeep(this.configuration.getProperties().profileLoginFields);
    if (this.appStateService.getAppState().hasNotification(AppConstants.SN_PASSWORD_CHANGED)) {
      this.showNotification(AppConstants.SN_PASSWORD_CHANGED);
    }
    if (this.appStateService.getAppState().hasNotification(AppConstants.SN_LOGIN_EMAIL_VALIDATED)) {
      this.showNotification(AppConstants.SN_LOGIN_EMAIL_VALIDATED);
    }
  }


  isAccepted(t: AcceptAgreementChallengeWithReceivedTime): boolean {
    const retVal = t && t.accepted && t.accepted > 0;
    return retVal;
  }
  isRejected(t: AcceptAgreementChallengeWithReceivedTime): boolean {
    const retVal = t && t.declined && t.declined > 0;
    return retVal;
  }

  public getRegionName(code: string): string {
    // Angular has once again made this seemingly trivial thing quite dfficult to figure out.
    // The templates have something called asyncPipe that is supposed to work with promises,
    // but apparently not with functions that return a promise. Could not figure out how to
    // use the existing async getRegionName from region service, so duplicating the localisation
    // resource access here. The complete localisation for the region-name.* relies on the
    // region-service code. This is just for accessing the resource and will not affect the
    // localisation files.
    const trans = this.translate;
    return trans.instant('region-name.' + this.profile.address.country + '-' + code);
  }

  getAgreementResponseTimeAsString(a: AcceptAgreementChallengeWithReceivedTime) {
    const format = 'longDate';
    const locale = this.translate.currentLang || this.translate.defaultLang;
    const formattedDate = formatDate(new Date(a.accepted || a.declined), format, locale);
    return formattedDate;
  }

  showNotification(key: string, data?: any) {
    this.notification = { key };
    if (!!data) {
      this.notification.data = { ...data };
    }
  }
  hideNotification() {
    if (this.notification && this.notification.key === AppConstants.SN_LOGIN_EMAIL_VALIDATED) {
      this.appStateService.getAppState().removeNotification(AppConstants.SN_LOGIN_EMAIL_VALIDATED);
    }
    if (this.notification && this.notification.key === AppConstants.SN_PASSWORD_CHANGED) {
      this.appStateService.getAppState().removeNotification(AppConstants.SN_PASSWORD_CHANGED);
    }
    this.notification = undefined;
  }
  onProfileLoaded(user: User) {
    this.profile = user;
  }
  ngOnInit() {
    super.init();
    const authState: AuthenticationState = this.appStateService.getAppState().getAuthenticationState();
    if (authState &&
        authState.getExistingAuthentications() &&
        authState.getExistingAuthentications().length) {
        if (!authState.getExistingAuthentications().find((val) => val.authenticationMethod === AppConstants.AC_AM_PASSWORD)) {
          // hide change password when there is no password
          this.loginFields = this.loginFields.filter((val) => val.key !== 'password');
        }
        if (!!authState.getExistingAuthentications().find((val) => val.authenticationMethod === AppConstants.AC_AM_TOTP)) {
          // hide totp when there is no totp, but don't remove from fields as it's still needed in edit
          this.hasTOTP = true;
        } else {
          this.hasTOTP = false;
        }
        if (authState.getExistingAuthentications().find((val) => val.authenticationMethod === AppConstants.AC_AM_DELEGATED)) {
          this.hasDelegatedAuthentication = true;
          // hide totp and make all fields readonly when there is delegated auth
          this.loginFields = this.loginFields.filter((val) => val.key !== 'totp').map((v) => ({ ...v, readonly: true }));
          this.personalFields = this.personalFields.map((v) => ({ ...v, readonly: true }));
          this.contactFields = this.contactFields.map((v) => ({ ...v, readonly: true }));
        }
    } else {
      throw new Error('Not authorized');
    }
    if (this.personalFields.filter((v) => !v.readonly).length > 0 ||
      this.contactFields.filter((v) => !v.readonly).length > 0 ||
      this.loginFields.filter((v) => !v.readonly).length > 0) {
      this.hasEditableFields = true;
    } else {
      this.hasEditableFields = false;
    }
    this.onProfileLoaded(this.appStateService.getAppState().getAuthenticationState().getProfile());

    this.navigationEndSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        StandaloneComponent.setFocusToElemOrChildControl(this.hostElement, this.focusSelectorPrefix);
      }
    });
    this.agreementLoadingFailedDismissed = false;
    // ITLIC-6317: Agreement visualisation disabled and replaced with external OneTrust link
    this.noAgreementsFound = true;
    /*
    listUsersAgreementsWithReceivedTime(
      this.agreementApi
    ).then((agr) => {
      if (!!agr) {
        this.userAgreements = agr.agreements;
        this.agreementLoadingFailed = false;
        if (this.userAgreements && this.userAgreements.length) {
          this.noAgreementsFound = false;
        } else {
          this.noAgreementsFound = true;
        }
      } else {
        this.noAgreementsFound = true;
      }
    }, (err) => {
      this.agreementLoadingFailed = true;
    });
    */
  }
  ngOnDestroy() {
    this.hideNotification();
  }
  getOneTrustLink() {
    const t = this.profile.userProperties?.find((f) => f.key === 'OnetrustLinkToken');
    return getOneTrustLink() +
      (!!t ? '/' + t.value : '');
  }
  signOut() {
    this.router.navigate([AppConstants.PATH_LOGOUT], {
      relativeTo: this.route,
    });
  }

  resolveDisplayName(): string|any {
    // we should never be here with a partial session
    return AppComponent.resolveUserName(this.profile, this.translate, this.configuration);
  }
  // itlic 7659 custom profilepic with initials
  resolveUserInitials(): string {
    // we should never be here with a partial session
    return AppComponent.resolveUserInitials(this.profile);
  }

  resolveCountryName(code: string): Observable<string> {
    return this.countries.getCountryName(code);
  }

  includes(list: SsoUIField[], value: string): boolean {
    return list.findIndex((val) => val.key === value) >= 0;
  }
  /**
   * Adds &#x200b; (wordbreak) char for each non alphanumeric character
   */
  addWordBreaks(val: string): string {
    return val ? val.replace(/(\W)/g, '<wbr />$1') : val;
  }

  public resolveWindowTitlePart(): Observable<string|undefined> {
    return this.translate.get('view-profile.window-title');
  }
}
